import type { ComponentType } from "react";
import { createBrowserRouter, Outlet, redirect } from "react-router";

import { DEV_LOGIN_AVAILABLE, dxmSsoConfig } from "src/auth/dxmSsoConfig";
import { AppShell } from "src/components/AppShell/AppShell";
import type { RouteHandle } from "src/components/AppShell/shellContext";
import { ROUTES } from "src/routes/paths";

const FULL_BLEED: RouteHandle = { fullBleed: true };
const FULL_WIDTH: RouteHandle = { fullWidth: true };

const lazyPage = (load: () => Promise<{ default: ComponentType }>) => async () => ({
	Component: (await load()).default
});

/**
 * Sign-in routes sit outside the shell; everything else is inside it and the shell shows the
 * sign-in page to signed-out visitors (there is no anonymous content).
 */
export const router = createBrowserRouter([
	{
		Component: Outlet,
		children: [
			...(DEV_LOGIN_AVAILABLE
				? [{ path: ROUTES.devLogin, lazy: lazyPage(() => import("src/pages/DevLoginPage/DevLoginPage")) }]
				: []),
			{ path: ROUTES.signInLink, lazy: lazyPage(() => import("src/pages/SignInLinkPage/SignInLinkPage")) },
			...(dxmSsoConfig
				? [
						{
							path: ROUTES.signInCallback,
							lazy: lazyPage(() => import("src/pages/SignInCallbackPage/SignInCallbackPage"))
						}
					]
				: []),
			{
				Component: AppShell,
				children: [
					{ path: ROUTES.home, lazy: lazyPage(() => import("src/pages/LaunchpadPage/LaunchpadPage")) },
					{ path: ROUTES.solutions, lazy: lazyPage(() => import("src/pages/SolutionsPage/SolutionsPage")) },
					{ path: ROUTES.solutionNew, lazy: lazyPage(() => import("src/pages/DescribePage/DescribePage")) },
					{
						path: "/solutions/:solutionId/design",
						handle: FULL_BLEED,
						lazy: lazyPage(() => import("src/pages/WorkspacePage/WorkspacePage"))
					},
					{
						path: "/solutions/:solutionId/overview",
						lazy: lazyPage(() => import("src/pages/OverviewPage/OverviewPage"))
					},
					{
						path: ROUTES.explore,
						handle: FULL_BLEED,
						lazy: lazyPage(() => import("src/pages/ExplorePage/ExplorePage"))
					},
					{ path: ROUTES.showcases, lazy: lazyPage(() => import("src/pages/ShowcasesPage/ShowcasesPage")) },
					{ path: "/showcases/:showcaseId", lazy: lazyPage(() => import("src/pages/ShowcasePage/ShowcasePage")) },
					{ path: ROUTES.companies, lazy: lazyPage(() => import("src/pages/CompaniesPage/CompaniesPage")) },
					{ path: "/companies/:companyId", lazy: lazyPage(() => import("src/pages/CompanyPage/CompanyPage")) },
					{ path: "/projects/:projectId", lazy: lazyPage(() => import("src/pages/ProjectPage/ProjectPage")) },
					{ path: "/solutions/:solutionId", lazy: lazyPage(() => import("src/pages/SolutionPage/SolutionPage")) },
					{ path: "/models/:modelId", lazy: lazyPage(() => import("src/pages/ModelPage/ModelPage")) },
					{ path: "/builds/:buildId", lazy: lazyPage(() => import("src/pages/BuildPage/BuildPage")) },
					{ path: ROUTES.activity, lazy: lazyPage(() => import("src/pages/ActivityPage/ActivityPage")) },
					{
						path: ROUTES.notificationSettings,
						lazy: lazyPage(() => import("src/pages/NotificationSettingsPage/NotificationSettingsPage"))
					},
					{
						path: ROUTES.members,
						handle: FULL_WIDTH,
						lazy: lazyPage(() => import("src/pages/MembersPage/MembersPage"))
					},
					{ path: ROUTES.admin, lazy: lazyPage(() => import("src/pages/AdminPage/AdminPage")) },
					{
						path: ROUTES.adminOrganizations,
						handle: FULL_WIDTH,
						lazy: lazyPage(() => import("src/pages/AdminOrganizationsPage/AdminOrganizationsPage"))
					},
					{
						path: ROUTES.adminUsers,
						handle: FULL_WIDTH,
						lazy: lazyPage(() => import("src/pages/AdminUsersPage/AdminUsersPage"))
					},
					{
						path: ROUTES.adminRoles,
						handle: FULL_WIDTH,
						lazy: lazyPage(() => import("src/pages/RolesPage/RolesPage"))
					},
					{
						path: ROUTES.adminLocationCatalog,
						handle: FULL_WIDTH,
						lazy: lazyPage(() => import("src/pages/LocationCatalogPage/LocationCatalogPage"))
					},
					{
						path: ROUTES.adminMarketing,
						handle: FULL_WIDTH,
						lazy: lazyPage(() => import("src/pages/MarketingResourcesPage/MarketingResourcesPage"))
					},
					{
						path: ROUTES.adminApiTokens,
						handle: FULL_WIDTH,
						lazy: lazyPage(() => import("src/pages/ApiTokensPage/ApiTokensPage"))
					},
					{
						path: ROUTES.adminFeatureFlags,
						handle: FULL_WIDTH,
						lazy: lazyPage(() => import("src/pages/FeatureFlagsPage/FeatureFlagsPage"))
					},
					{ path: ROUTES.legacyPlatform, loader: () => redirect(ROUTES.adminOrganizations) },
					{ path: ROUTES.legacyRoles, loader: () => redirect(ROUTES.adminRoles) },
					{ path: ROUTES.legacyApiTokens, loader: () => redirect(ROUTES.adminApiTokens) },
					{ path: "*", lazy: lazyPage(() => import("src/pages/NotFoundPage/NotFoundPage")) }
				]
			}
		]
	}
]);
